import { useEffect } from 'react';
import queryString from 'query-string';

import { useLogs } from './api';
import { LogsFilters } from './types';
import { LogsTable } from './components/LogsTable/LogsTable';
import Filters from './components/Filters/Filters';
import { LogsShowMore } from './components/LogsShowMore/LogsShowMore';

import { useLogsFilters } from './hooks/useLogsFilters';
import { useAppDrawer } from '../../components/AppDrawer/AppDrawerProvider';
import { getLogsRangeFilter } from './utils';

interface LogsDrawerProps {
    filters: LogsFilters;
    excludedFilters?: (keyof LogsFilters)[];
    logsTableTitle: string;
    maxEndDate?: Date;
}

const buildLogsUrlFromFilters = (
    filters: LogsFilters,
    excludedFilters: (keyof LogsFilters)[],
    maxEndDate?: Date
): string => {
    const { timeframe = '30', ...rest } = filters;

    return `/Logs?${queryString.stringify(
        {
            ...rest,
            range: getLogsRangeFilter(timeframe, maxEndDate),
            excludedFilters
        },
        { arrayFormat: 'comma' }
    )}`;
};

const LogsDrawer = ({ filters, excludedFilters = [], logsTableTitle, maxEndDate }: LogsDrawerProps) => {
    const { updateAppDrawerProps, closeAppDrawer } = useAppDrawer();

    const { data, isLoading, mutate: fetchLogs } = useLogs();
    const { queryFilters, setQueryFilters } = useLogsFilters(filters);

    const handleShowMore = () => {
        window.open(buildLogsUrlFromFilters(queryFilters, excludedFilters, maxEndDate), '_blank');
    };

    useEffect(() => {
        fetchLogs({ filters: queryFilters, excludedFilters, maxEndDate });
    }, [queryFilters, fetchLogs, filters, maxEndDate, excludedFilters]);

    useEffect(() => {
        updateAppDrawerProps({
            headerTitle: 'Authentication Logs',
            headerClassName: 'border-b border-sf-gray-100',
            onClose: closeAppDrawer
        });
    }, []);

    return (
        <div className="flex flex-col justify-between">
            <div className="w-[800px] h-fit font-sf text-sf-gray-800 flex flex-col justify-between p-6 gap-[32px]">
                <Filters filters={queryFilters} onFiltersChange={setQueryFilters} />
                <div className="flex flex-col gap-4">
                    <LogsTable
                        title={logsTableTitle}
                        logs={data?.logs ?? []}
                        count={data?.logsCount ?? 0}
                        defaultDestinations={data?.defaultDestinations ?? {}}
                        wallNames={data?.wallNames ?? {}}
                        hiddenRiskIndicators={data?.hiddenRiskIndicators ?? []}
                        loading={isLoading}
                    />
                    <LogsShowMore onClick={handleShowMore} />
                </div>
            </div>
        </div>
    );
};

export default LogsDrawer;
